import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { AngularFireRemoteConfig } from '@angular/fire/compat/remote-config';
import { FireBaseModel } from '@core/models/firebase.model';
import { environment } from '@environment/environment';
import { BehaviorSubject } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class RemoteConfigService implements OnDestroy {
  private isTransmitEnabled = new BehaviorSubject<boolean>(false);
  private isTrustedDeviceJourneyEnabled = new BehaviorSubject<boolean>(false);
  public isTransmitEnabled$ = this.isTransmitEnabled.asObservable().pipe(distinctUntilChanged());
  public isTrustedDeviceJourneyEnabled$ = this.isTrustedDeviceJourneyEnabled
    .asObservable()
    .pipe(distinctUntilChanged());
  private configInitializationPromise?: Promise<void>;
  private isRemoteConfigInitialized = false;
  private isRefreshInProgress = false;

  constructor(
    private remoteConfig: AngularFireRemoteConfig,
    private zone: NgZone,
  ) {}

  ngOnDestroy(): void {}

  public initializeRemoteConfig(): Promise<void> {
    if (!this.configInitializationPromise) {
      this.configInitializationPromise = this.activateRemoteConfig();
    }

    return this.configInitializationPromise;
  }

  private async activateRemoteConfig(): Promise<void> {
    await this.refreshRemoteConfig();
  }

  private async refreshRemoteConfig(): Promise<void> {
    // Only fetch once per page load: if already initialized, do nothing.
    if (this.isRemoteConfigInitialized) {
      return;
    }

    if (this.isRefreshInProgress) {
      return;
    }

    this.isRefreshInProgress = true;

    try {
      if (!this.isRemoteConfigInitialized) {
        await this.remoteConfig.ensureInitialized();
        this.isRemoteConfigInitialized = true;
      }

      await this.remoteConfig.fetchAndActivate();
      const mosaicVisibilityFlag = await this.remoteConfig.getString(
        environment.remoteConfig.buildFlavorKey,
      );
      this.applyRemoteConfig(mosaicVisibilityFlag);
    } catch (error) {
      console.error('No se pudo inicializar o activar Remote Config:', error);
    } finally {
      this.isRefreshInProgress = false;
    }
  }


  private applyRemoteConfig(mosaicVisibilityFlag: string | null): void {
    if (!mosaicVisibilityFlag) {
      return;
    }

    try {
      const raw = JSON.parse(mosaicVisibilityFlag) as Partial<FireBaseModel>;

      const showMosaicFlag: FireBaseModel = {
        certificatesDerBase64: raw.certificatesDerBase64 ?? [],
        isTransmitEnabled: !!raw.isTransmitEnabled,
        isTrustedDeviceJourneyEnabled: !!raw.isTrustedDeviceJourneyEnabled,
        disableTransmitFor: raw.disableTransmitFor ?? [],
        build_flavor: raw.build_flavor ?? '',
      };

      if (!environment.production) {
        console.log(
          '* / RemoteConfig / showMosaicFlag:',
          'isTransmitEnabled: ',
          showMosaicFlag.isTransmitEnabled,
          'isTrustedDeviceJourneyEnabled: ',
          showMosaicFlag.isTrustedDeviceJourneyEnabled,
        );
      }

      this.zone.run(() => {
        this.isTransmitEnabled.next(showMosaicFlag.isTransmitEnabled);
        this.isTrustedDeviceJourneyEnabled.next(showMosaicFlag.isTrustedDeviceJourneyEnabled);
      });
    } catch (error) {
      console.error('No se pudo aplicar la configuración de Remote Config:', error);
    }
  }
}
